const {
  DEFAULT_LESSON_TYPES,
  normalizeLessonTypesSetting,
} = require("./lessonTypes");

const SETTING_IDS = {
  PUBLIC_TIMETABLE: 'publicTimetable',
  SUPPORT_REPORTING: 'supportReporting',
  PAYMENT_REMINDER_RULES: "paymentReminderRules",
  PAYMENT_WATCHDOG_FOLLOW_UPS: "paymentWatchdogFollowUps",
  SCHOOL_CONTACT: "schoolContact",
  CANCELLATION_POLICY: "cancellationPolicy",
  DASHBOARD_PREFERENCES: "dashboardPreferences",
  PACKAGE_POLICY: "packagePolicy",
  LESSON_TYPES: "lessonTypes",
};

const DEFAULT_PAYMENT_REMINDER_RULES = {
  id: SETTING_IDS.PAYMENT_REMINDER_RULES,
  enabled: true,
  minClientIntervalHours: 24,
  firstOverdueReminderDaysAfterDueDate: 2,
  nextOverdueReminderDaysAfterPrevious: 7,
  maxOverdueReminders: 3,
};

const DEFAULT_SCHOOL_CONTACT = {
  id: SETTING_IDS.SCHOOL_CONTACT,
  email: "szkola@extra-english.pl",
  phone: "(+48) 575 400 141",
  address: "Wypoczynkowa 43, 62-081 Baranowo",
  mapsUrl: "https://www.google.com/maps/search/?api=1&query=Extra%20English%20Wypoczynkowa%2043%2062-081%20Baranowo",
};

const DEFAULT_PAYMENT_WATCHDOG_FOLLOW_UPS = {
  id: SETTING_IDS.PAYMENT_WATCHDOG_FOLLOW_UPS,
  cases: {},
};

const DEFAULT_CANCELLATION_POLICY = {
  id: SETTING_IDS.CANCELLATION_POLICY,
  enabled: true,
  cancellationCutoffHours: 24,
};

const DEFAULT_DASHBOARD_PREFERENCES = {
  id: SETTING_IDS.DASHBOARD_PREFERENCES,
  returnStateTtlHours: 6,
};

const DEFAULT_PACKAGE_POLICY = {
  id: SETTING_IDS.PACKAGE_POLICY,
  pendingPaymentDeadlineDays: 7,
  pendingPaymentReminderDaysBeforeDue: 2,
  endingSoonThresholdUnits: 2,
  emailStudentOnEndingPackage: false,
};

function buildSettingsStorageId(schoolId, settingId) {
  return schoolId ? `${schoolId}#${settingId}` : settingId;
}

function getSettingLogicalId(record = {}) {
  if (record.settingId) return record.settingId;
  return record.id;
}

function toPositiveInteger(value, fallback) {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return fallback;
  return parsed;
}

function nonEmptyString(value, fallback) {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function normalizePaymentReminderRules(record = {}) {
  if (record.minClientIntervalHours !== undefined && (!Number.isInteger(Number(record.minClientIntervalHours)) || Number(record.minClientIntervalHours) < 24)) {
    throw Object.assign(new Error('Minimalny odstęp przypomnień wynosi 24 godziny'), { statusCode: 400 });
  }
  return {
    ...DEFAULT_PAYMENT_REMINDER_RULES,
    ...record,
    id: SETTING_IDS.PAYMENT_REMINDER_RULES,
    enabled: record.enabled !== false,
    minClientIntervalHours: record.minClientIntervalHours === undefined ? 24 : Number(record.minClientIntervalHours),
    firstOverdueReminderDaysAfterDueDate: toPositiveInteger(
      record.firstOverdueReminderDaysAfterDueDate,
      DEFAULT_PAYMENT_REMINDER_RULES.firstOverdueReminderDaysAfterDueDate,
    ),
    nextOverdueReminderDaysAfterPrevious: toPositiveInteger(
      record.nextOverdueReminderDaysAfterPrevious,
      DEFAULT_PAYMENT_REMINDER_RULES.nextOverdueReminderDaysAfterPrevious,
    ),
    maxOverdueReminders: toPositiveInteger(
      record.maxOverdueReminders,
      DEFAULT_PAYMENT_REMINDER_RULES.maxOverdueReminders,
    ),
  };
}

function normalizeSchoolContact(record = {}) {
  return {
    ...DEFAULT_SCHOOL_CONTACT,
    ...record,
    id: SETTING_IDS.SCHOOL_CONTACT,
    email: nonEmptyString(record.email, DEFAULT_SCHOOL_CONTACT.email),
    phone: nonEmptyString(record.phone, DEFAULT_SCHOOL_CONTACT.phone),
    address: nonEmptyString(record.address, DEFAULT_SCHOOL_CONTACT.address),
    mapsUrl: nonEmptyString(record.mapsUrl, DEFAULT_SCHOOL_CONTACT.mapsUrl),
  };
}

function normalizePaymentWatchdogFollowUps(record = {}) {
  return {
    ...DEFAULT_PAYMENT_WATCHDOG_FOLLOW_UPS,
    ...record,
    id: SETTING_IDS.PAYMENT_WATCHDOG_FOLLOW_UPS,
    cases: record.cases && typeof record.cases === "object" && !Array.isArray(record.cases)
      ? record.cases
      : {},
  };
}

function normalizeCancellationPolicy(record = {}) {
  return {
    ...DEFAULT_CANCELLATION_POLICY,
    ...record,
    id: SETTING_IDS.CANCELLATION_POLICY,
    enabled: record.enabled !== false,
    cancellationCutoffHours: toPositiveInteger(
      record.cancellationCutoffHours,
      DEFAULT_CANCELLATION_POLICY.cancellationCutoffHours,
    ),
  };
}

function normalizeDashboardPreferences(record = {}) {
  return {
    ...DEFAULT_DASHBOARD_PREFERENCES,
    ...record,
    id: SETTING_IDS.DASHBOARD_PREFERENCES,
    returnStateTtlHours: toPositiveInteger(
      record.returnStateTtlHours,
      DEFAULT_DASHBOARD_PREFERENCES.returnStateTtlHours,
    ),
  };
}

function normalizePackagePolicy(record = {}) {
  return {
    ...DEFAULT_PACKAGE_POLICY,
    ...record,
    id: SETTING_IDS.PACKAGE_POLICY,
    pendingPaymentDeadlineDays: toPositiveInteger(
      record.pendingPaymentDeadlineDays,
      DEFAULT_PACKAGE_POLICY.pendingPaymentDeadlineDays,
    ),
    pendingPaymentReminderDaysBeforeDue: toPositiveInteger(
      record.pendingPaymentReminderDaysBeforeDue,
      DEFAULT_PACKAGE_POLICY.pendingPaymentReminderDaysBeforeDue,
    ),
    endingSoonThresholdUnits: toPositiveInteger(
      record.endingSoonThresholdUnits,
      DEFAULT_PACKAGE_POLICY.endingSoonThresholdUnits,
    ),
    emailStudentOnEndingPackage: record.emailStudentOnEndingPackage === true,
  };
}

const normalizeLessonTypes = normalizeLessonTypesSetting;

function normalizeSettingsRecord(record = {}) {
  const logicalId = getSettingLogicalId(record);
  if (logicalId === SETTING_IDS.PUBLIC_TIMETABLE) {
    if (record.showIndividualLessons !== undefined && typeof record.showIndividualLessons !== 'boolean') {
      throw Object.assign(new Error('Widoczność zajęć indywidualnych musi być wartością logiczną.'), { statusCode: 400 });
    }
    return { ...record, id: logicalId, showIndividualLessons: record.showIndividualLessons !== false };
  }
  if (logicalId === SETTING_IDS.SUPPORT_REPORTING) {
    if (record.enabled !== undefined && typeof record.enabled !== 'boolean') {
      throw Object.assign(new Error('Wartość enabled musi być logiczna.'), { statusCode: 400 });
    }
    return { ...record, id: logicalId, enabled: record.enabled !== false };
  }
  if (logicalId === SETTING_IDS.PAYMENT_REMINDER_RULES) return normalizePaymentReminderRules({ ...record, id: logicalId });
  if (logicalId === SETTING_IDS.PAYMENT_WATCHDOG_FOLLOW_UPS) return normalizePaymentWatchdogFollowUps({ ...record, id: logicalId });
  if (logicalId === SETTING_IDS.SCHOOL_CONTACT) return normalizeSchoolContact({ ...record, id: logicalId });
  if (logicalId === SETTING_IDS.CANCELLATION_POLICY) return normalizeCancellationPolicy({ ...record, id: logicalId });
  if (logicalId === SETTING_IDS.DASHBOARD_PREFERENCES) return normalizeDashboardPreferences({ ...record, id: logicalId });
  if (logicalId === SETTING_IDS.PACKAGE_POLICY) return normalizePackagePolicy({ ...record, id: logicalId });
  if (logicalId === SETTING_IDS.LESSON_TYPES) return normalizeLessonTypes({ ...record, id: logicalId });
  return record;
}

function mergeSettingsWithDefaults(records = []) {
  const byId = records.reduce((acc, record) => ({ ...acc, [getSettingLogicalId(record)]: record }), {});
  return [
    normalizeSettingsRecord({ ...byId[SETTING_IDS.PUBLIC_TIMETABLE], id: SETTING_IDS.PUBLIC_TIMETABLE }),
    normalizeSettingsRecord({ ...byId[SETTING_IDS.SUPPORT_REPORTING], id: SETTING_IDS.SUPPORT_REPORTING }),
    normalizePaymentReminderRules(byId[SETTING_IDS.PAYMENT_REMINDER_RULES]),
    normalizePaymentWatchdogFollowUps(byId[SETTING_IDS.PAYMENT_WATCHDOG_FOLLOW_UPS]),
    normalizeSchoolContact(byId[SETTING_IDS.SCHOOL_CONTACT]),
    normalizeCancellationPolicy(byId[SETTING_IDS.CANCELLATION_POLICY]),
    normalizeDashboardPreferences(byId[SETTING_IDS.DASHBOARD_PREFERENCES]),
    normalizePackagePolicy(byId[SETTING_IDS.PACKAGE_POLICY]),
    normalizeLessonTypes(byId[SETTING_IDS.LESSON_TYPES]),
  ];
}

module.exports = {
  SETTING_IDS,
  DEFAULT_PAYMENT_REMINDER_RULES,
  DEFAULT_PAYMENT_WATCHDOG_FOLLOW_UPS,
  DEFAULT_SCHOOL_CONTACT,
  DEFAULT_CANCELLATION_POLICY,
  DEFAULT_DASHBOARD_PREFERENCES,
  DEFAULT_PACKAGE_POLICY,
  DEFAULT_LESSON_TYPES,
  buildSettingsStorageId,
  getSettingLogicalId,
  normalizePaymentReminderRules,
  normalizePaymentWatchdogFollowUps,
  normalizeSchoolContact,
  normalizeCancellationPolicy,
  normalizeDashboardPreferences,
  normalizePackagePolicy,
  normalizeLessonTypes,
  normalizeSettingsRecord,
  mergeSettingsWithDefaults,
};
